import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, map, switchMap, take, filter } from 'rxjs/operators';
import { throwError, BehaviorSubject } from 'rxjs';
import { EnvironmentService } from '../services/environment.service';
import { AUTH_SERVICE_TOKEN } from '../services/auth/auth.interface';

export const httpAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AUTH_SERVICE_TOKEN);
  const environmentService = inject(EnvironmentService);

  let isRefreshing = false;
  const refreshTokenSubject = new BehaviorSubject<string | null>(null);

  // Skip the interceptor for auth-related endpoints to avoid circular dependency
  const skipUrls = ['/auth/token', '/auth/refresh-token', '/auth/login-auth-code', '/auth/login-url'];
  const shouldSkip = skipUrls.some(url => req.url.includes(url));
  if (shouldSkip) {
    return next(req.clone({ withCredentials: true }));
  }

  // Skip the interceptor for local asset requests (e.g. translation files)
  if (req.url.startsWith('/assets/')) {
    return next(req);
  }

  const handleResponseError = (error: any) => {
    let errorMsg = `Error Code: ${error.status}, Message: ${error.message}`;

    // Business error
    if (error.status === 400) {
      console.error(errorMsg);
    }
    // Invalid token error
    else if (error.status === 401) {
      const currentUrl = router.url;

      // Skip refresh logic for /login and /auth/refresh-token endpoints
      if (currentUrl === '/login' || req.url.includes('/auth/refresh-token')) {
        return throwError(() => error);
      }

      if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenSubject.next(null);

        return authService.refreshToken().pipe(
          take(1),
          switchMap(() => {
            isRefreshing = false;
            refreshTokenSubject.next('refreshed');
            return next(authService.injectAuth(req.clone()));
          }),
          catchError(refreshError => {
            isRefreshing = false;
            router.navigate(['/login']);
            return throwError(() => refreshError);
          })
        );
      } else {
        // Queue concurrent requests until the token is refreshed
        return refreshTokenSubject.pipe(
          filter(token => token !== null),
          take(1),
          switchMap(() => {
            return next(authService.injectAuth(req.clone()));
          })
        );
      }
    }
    // Forbidden error
    else if (error.status === 403) {
      if (environmentService.isEmbedded) {
        console.error("Access forbidden - embedded mode");
        // TODO: Navigate to an error page within the embedded app
      } else {
        router.navigate(['/login']);
      }
    }
    // Server error
    else if (error.status === 500) {
      console.error(errorMsg);
    }
    // Maintenance error
    else if (error.status === 503) {
      console.error(errorMsg);
    }

    return throwError(() => error);
  };
  return authService.refreshToken().pipe(
    take(1),
    switchMap(() => {
      return next(authService.injectAuth(req));
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.error instanceof ErrorEvent) {
        return throwError(() => new Error(`Frontend error: ${error.error.message}`));
      } else {
        return handleResponseError(error);
      }
    })
  );
};
